import { useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (tries >= 8) { setState("pending"); return; }
        tries += 1;
        timer = setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-10 max-w-md w-full text-center" data-testid="payment-success-card">
        <Link to="/" className="inline-flex mb-6"><img src="/logo.jpg" alt="Solar Stars LLC" className="h-14 w-auto object-contain" /></Link>
        {state === "checking" && (<><Loader2 className="w-14 h-14 text-emerald-600 animate-spin mx-auto" /><h1 className="font-heading text-2xl font-black text-slate-900 mt-5">Confirming your payment…</h1><p className="text-slate-500 mt-2">Hang tight, this only takes a moment.</p></>)}
        {state === "paid" && (<><CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto" /><h1 className="font-heading text-2xl font-black text-slate-900 mt-5">Booking confirmed!</h1><p className="text-slate-500 mt-2">Your cleaning is scheduled and paid. We'll see you soon.</p><Button onClick={() => navigate("/dashboard")} data-testid="success-dashboard-btn" className="mt-6 rounded-full bg-emerald-700 hover:bg-emerald-800 w-full h-11">Go to my dashboard</Button></>)}
        {state === "pending" && (<><Loader2 className="w-14 h-14 text-amber-500 mx-auto" /><h1 className="font-heading text-2xl font-black text-slate-900 mt-5">Payment processing</h1><p className="text-slate-500 mt-2">It's taking a little longer than usual. Check your dashboard shortly.</p><Button onClick={() => navigate("/dashboard")} className="mt-6 rounded-full bg-emerald-700 hover:bg-emerald-800 w-full h-11">Go to dashboard</Button></>)}
        {state === "error" && (<><XCircle className="w-16 h-16 text-red-500 mx-auto" /><h1 className="font-heading text-2xl font-black text-slate-900 mt-5">Something went wrong</h1><p className="text-slate-500 mt-2">We couldn't confirm this payment. Please check your dashboard.</p><Button onClick={() => navigate("/dashboard")} className="mt-6 rounded-full w-full h-11">Go to dashboard</Button></>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-10 max-w-md w-full text-center" data-testid="payment-cancel-card">
        <XCircle className="w-16 h-16 text-slate-400 mx-auto" />
        <h1 className="font-heading text-2xl font-black text-slate-900 mt-5">Payment cancelled</h1>
        <p className="text-slate-500 mt-2">No charge was made. Your booking is saved as unpaid — you can pay anytime from your dashboard.</p>
        <div className="flex gap-3 mt-6">
          <Button onClick={() => navigate("/dashboard")} variant="outline" className="flex-1 rounded-full h-11">Dashboard</Button>
          <Button onClick={() => navigate("/book")} className="flex-1 rounded-full bg-emerald-700 hover:bg-emerald-800 h-11">Try again</Button>
        </div>
      </div>
    </div>
  );
}
